import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, getToken, API } from "@/lib/api";
import { toast } from "sonner";
import { Save, ArrowLeft, Upload, Camera, Lock, Trash2, Check, X as XIcon, Plus } from "lucide-react";

export default function Profile() {
  const { user, updateProfile, refreshMe } = useAuth();
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const privateInputRef = useRef(null);

  const [form, setForm] = useState({
    username: user.username || "",
    bio: user.bio || "",
    age: user.age ?? "",
    location: user.location || "",
    pronouns: user.pronouns || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [privateImages, setPrivateImages] = useState([]);
  const [privateBusy, setPrivateBusy] = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    loadPrivate();
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPrivate = async () => {
    try {
      const { data } = await api.get("/profile/private-images");
      setPrivateImages(data);
    } catch {}
  };

  const loadRequests = async () => {
    try {
      const { data } = await api.get("/me/private-access/incoming");
      setRequests(data);
    } catch {}
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshMe();
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const onPickPrivate = () => privateInputRef.current?.click();

  const onPrivateFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setPrivateBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/private-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadPrivate();
      toast.success("Private picture saved");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPrivateBusy(false);
    }
  };

  const deletePrivate = async (id) => {
    try {
      await api.delete(`/profile/private-images/${id}`);
      setPrivateImages((prev) => prev.filter((p) => p.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const decideRequest = async (reqId, action) => {
    try {
      await api.post(`/me/private-access/${reqId}/${action}`);
      setRequests((prev) => prev.filter((r) => r.requester.id !== reqId));
      toast.success(action === "approve" ? "Access granted" : "Request denied");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        username: form.username,
        bio: form.bio,
        age: form.age === "" ? null : Number(form.age),
        location: form.location,
        pronouns: form.pronouns,
      };
      await updateProfile(payload);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => nav("/lobby")} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-6" data-testid="profile-back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="flex items-center gap-5 mb-10">
          <div className="relative">
            <Avatar user={user} size={84} />
            <button
              onClick={onPickFile}
              disabled={uploading}
              data-testid="avatar-upload-btn"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#e81cff] text-white flex items-center justify-center shadow-[0_0_16px_rgba(232,28,255,0.5)] hover:bg-[#ff4dff] transition disabled:opacity-60"
              aria-label="Change profile picture"
            >
              {uploading ? <Upload className="w-4 h-4 animate-pulse" /> : <Camera className="w-4 h-4" />}
            </button>
            <input
              ref={fileInputRef}
              data-testid="avatar-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={onFileChange}
              className="hidden"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400">
              {user.role === "admin" ? "Admin" : user.is_guest ? "Guest identity" : "Member"}
            </div>
            <h1 className="font-display text-4xl font-black tracking-tighter mt-1">{user.username}</h1>
            {user.email && <div className="text-sm text-zinc-500 mt-1">{user.email}</div>}
            <div className="text-xs text-zinc-600 mt-1">Tap the camera to upload a photo (JPG/PNG/WEBP · 5MB max)</div>
          </div>
        </div>

        <form onSubmit={save} className="space-y-5 rounded-3xl border border-white/5 bg-[#0c0c0f] p-6 sm:p-8">
          <Field label="Username" required>
            <input data-testid="profile-username-input" required className="text-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </Field>
          <Field label="Bio">
            <textarea data-testid="profile-bio-input" className="text-input min-h-[90px] resize-none" maxLength={280} placeholder="A line or two about you" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            <div className="text-[10px] text-zinc-600 mt-1 text-right">{form.bio.length}/280</div>
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Age">
              <input data-testid="profile-age-input" type="number" min={18} max={120} className="text-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
            </Field>
            <Field label="Pronouns">
              <input data-testid="profile-pronouns-input" placeholder="he/him, she/her, they/them…" className="text-input" value={form.pronouns} onChange={(e) => setForm({ ...form, pronouns: e.target.value })} />
            </Field>
            <Field label="Location">
              <input data-testid="profile-location-input" placeholder="City, State (e.g., Miami, FL)" className="text-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </Field>
          </div>

          <button data-testid="profile-save-btn" disabled={saving} className="neon-btn inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save profile"}
          </button>
        </form>

        {/* Private album — 2 slots */}
        <section className="mt-10 rounded-3xl border border-white/5 bg-[#0c0c0f] p-6 sm:p-8" data-testid="private-album-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-fuchsia-400" />
              <h2 className="font-display text-xl font-bold tracking-tight">Private album</h2>
            </div>
            <span className="text-xs text-zinc-500">{privateImages.length}/2 slots</span>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            Two pictures only people you approve can see. Requests show up below.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {privateImages.map((p) => (
              <PrivateTile key={p.id} pic={p} onDelete={() => deletePrivate(p.id)} />
            ))}
            {privateImages.length < 2 && (
              <button
                type="button"
                onClick={onPickPrivate}
                disabled={privateBusy}
                data-testid="upload-private-btn"
                className="aspect-square rounded-2xl border border-dashed border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-fuchsia-500/30 text-zinc-400 hover:text-white flex flex-col items-center justify-center gap-2 transition disabled:opacity-60"
              >
                <Plus className="w-5 h-5" />
                <span className="text-xs">{privateBusy ? "Uploading…" : "Add private picture"}</span>
              </button>
            )}
          </div>
          <input
            ref={privateInputRef}
            data-testid="private-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onPrivateFileChange}
            className="hidden"
          />
        </section>

        {/* Incoming private-access requests */}
        {requests.length > 0 && (
          <section className="mt-6 rounded-3xl border border-fuchsia-500/20 bg-fuchsia-500/[0.03] p-6 sm:p-8" data-testid="private-requests-section">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-fuchsia-400" />
              <h2 className="font-display text-lg font-bold tracking-tight">Private access requests</h2>
            </div>
            <div className="space-y-2">
              {requests.map((r) => (
                <div
                  key={r.requester.id}
                  data-testid={`private-request-${r.requester.id}`}
                  className="flex items-center gap-3 rounded-2xl bg-[#0c0c0f] border border-white/5 p-3"
                >
                  <Avatar user={r.requester} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{r.requester.username}</div>
                    <div className="text-[10px] text-zinc-500">wants to see your private pics</div>
                  </div>
                  <button
                    onClick={() => decideRequest(r.requester.id, "approve")}
                    data-testid={`approve-${r.requester.id}`}
                    className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 inline-flex items-center justify-center"
                    aria-label="Approve"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => decideRequest(r.requester.id, "deny")}
                    data-testid={`deny-${r.requester.id}`}
                    className="w-9 h-9 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 inline-flex items-center justify-center"
                    aria-label="Deny"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function PrivateTile({ pic, onDelete }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    const token = getToken();
    let url = null;
    let cancelled = false;
    fetch(`${API}/files/${pic.storage_path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("fetch failed");
        const blob = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [pic.storage_path]);
  return (
    <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0c] group" data-testid={`private-tile-${pic.id}`}>
      {blobUrl ? (
        <img src={blobUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full animate-pulse bg-white/5" />
      )}
      <button
        type="button"
        onClick={onDelete}
        data-testid={`delete-private-${pic.id}`}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-rose-300 hover:bg-rose-500 hover:text-white inline-flex items-center justify-center opacity-90 transition"
        aria-label="Remove"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-semibold">
        {label} {required && <span className="text-fuchsia-400">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
